import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useSchool } from '../contexts/SchoolContext';
import { MODULE_BY_ID } from '../modules/registry';
import type { ModuleId, UserRole } from '../types';

interface ModuleRouteProps {
  module: ModuleId;
  /** Narrow access further than the module default if given */
  roles?: UserRole[];
}

/**
 * Route guard combining role checks with module entitlement.
 * Unentitled users land back on the launcher, which shows the module as locked.
 */
export default function ModuleRoute({ module, roles }: ModuleRouteProps) {
  const { appUser, loading } = useAuth();
  const { hasModule, schoolLoading } = useSchool();

  if (loading || schoolLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-600 border-t-transparent" />
      </div>
    );
  }

  if (!appUser) return <Navigate to="/login" replace />;

  const allowed = roles ?? MODULE_BY_ID[module].roles;
  if (appUser.role !== 'superAdmin' && !allowed.includes(appUser.role)) {
    return <Navigate to="/home" replace />;
  }

  if (!hasModule(module)) return <Navigate to="/home" replace />;

  return <Outlet />;
}
